import React from 'react';
import { useTranslation } from 'react-i18next';

const excerpt = (value, length = 220) => {
  const text = `${value ?? ''}`.trim().replace(/\s+/gu, ' ');
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

/**
 * What the agent's parameters amount to, without opening them.
 *
 * Derived from shapes rather than from agent keys — a list of criteria, a map of per-question
 * overrides — so a new agent gets a readable summary without this file knowing about it.
 */
const AgentParamsSummary = ({ agent, params }) => {
  const { t } = useTranslation();
  const summaryField = (agent?.ui_descriptor?.summaryFields || [])[0]?.replace(/^params\./, '');
  const prompt = summaryField ? params?.[summaryField] : null;

  const facts = [];
  Object.entries(params || {}).forEach(([name, value]) => {
    if (name === summaryField) return;
    if (Array.isArray(value)) {
      facts.push(t('{{count}} {{field}}', { count: value.length, field: name.replace(/_/g, ' ') }));
      return;
    }
    if (value && typeof value === 'object' && Array.isArray(value.rules)) {
      if (value.rules.length) facts.push(t('{{count}} combination(s)', { count: value.rules.length }));
      return;
    }
    if (value && typeof value === 'object') {
      const configured = Object.keys(value).length;
      if (configured) facts.push(t('{{count}} {{field}}', { count: configured, field: name.replace(/_/g, ' ') }));
      return;
    }
    if (typeof value === 'string' && value) facts.push(value);
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
      {prompt
        ? <p className="text-sm italic leading-6 text-gray-700 dark:text-gray-300">“{excerpt(prompt)}”</p>
        : <p className="text-sm text-gray-500 dark:text-gray-400">{t('Not configured yet.')}</p>}
      {facts.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {facts.map(fact => (
            <span key={fact} className="rounded-full bg-white px-2 py-0.5 text-xs text-gray-600 shadow-sm dark:bg-gray-900 dark:text-gray-300">{fact}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentParamsSummary;
